import validateEmail from "./validateEmail";
import { validateURL } from "./validateURL";

const isUsaSelected = (countries) =>
  Array.isArray(countries) && countries.length === 1 && countries[0] === "USA";

const hasRequiredUsState = (countries, usState, isNew) =>
  !isNew || !isUsaSelected(countries) || !!usState?.length;

const getErrors = (state, keys) =>
  Object.entries(state)
    .filter(([key]) => keys.includes(key))
    .filter(([key, value]) => {
      if (
        ["name", "address", "countries", "organizationFields"].includes(key)
      ) {
        return !value?.length;
      }
      if (["problem"].includes(key)) {
        return !Object.keys(value)?.length;
      }
      if (["photo", "source"].includes(key)) {
        return !validateURL(value);
      }

      return key !== "email" && !value?.length;
    })
    .map(([key]) => key);

const checkIsValidSearch = ({ state }) => {
  const type = state?.searchType[0];
  if (type === "NGO" || type === "hotSpot") {
    return !!(state.organizationFields.length && state.countries.length);
  }
  if (type === "business") {
    return !!(state.businessFields.length && state.countries.length);
  }
  if (type === "media" || type === "government") {
    return !!state.countries.length;
  }

  return false;
};

const updateErrorsSearch = ({ state, setValidationError }) => {
  const type = state.searchType[0];
  if (!type) {
    setValidationError(["searchType"]);
  }
  if (type === "hotSpots") {
    const keys = ["countries", "searchType", "organizationFields"];
    const errors = getErrors(state, keys, setValidationError);
    setValidationError(errors);
  }
  if (type === "NGO" || type === "hotSpots") {
    const keys = ["countries", "searchType", "organizationFields"];
    const errors = getErrors(state, keys, setValidationError);
    setValidationError(errors);
  }

  if (type === "business") {
    const keys = ["countries", "searchType", "businessFields"];
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
  if (type === "media" || type === "government") {
    const keys = ["countries", "searchType"];
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
};

const checkIsValidForm = ({ state }) => {
  const {
    name,
    countries,
    usState,
    address,
    organizationFields,
    businessFields,
    category,
    type,
    hotSpotFields,
    greenSpotFields,
    website,
  } = state;
  if (type === "NGO") {
    return !!(
      organizationFields.length &&
      countries.length &&
      name.length &&
      website.length &&
      address.length &&
      hasRequiredUsState(countries, usState, state.isNew)
    );
  }
  if (type === "business") {
    return !!(
      businessFields.length &&
      category &&
      countries.length &&
      name &&
      website &&
      address &&
      hasRequiredUsState(countries, usState, state.isNew)
    );
  }
  if (type === "media" || type === "government") {
    return (
      !!countries.length &&
      name &&
      website &&
      address &&
      hasRequiredUsState(countries, usState, state.isNew)
    );
  }
  if (type === "news") {
    return !!(
      state.title &&
      state.countries.length &&
      state.newsFields.length &&
      state.body &&
      state.photoSource &&
      state.source &&
      validateURL(state.photo)
    );
  }
  if (type === "blog") {
    return !!(
      state.title &&
      state.countries.length &&
      state.blogFields.length &&
      state.photoSource &&
      state.body &&
      validateURL(state.photo)
    );
  }
  if (type === "hotSpot") {
    return !!(
      name?.length &&
      hotSpotFields?.length &&
      countries?.length &&
      address?.length
    );
  }
  if (type === "greenSpot") {
    return !!(
      name?.length &&
      greenSpotFields?.length &&
      countries?.length &&
      address?.length
    );
  }
  return false;
};

const updateErrorsForm = ({ state, setValidationError }) => {
  const { type } = state;

  if (!type) {
    setValidationError(["type"]);
  }
  if (type === "NGO") {
    const keys = [
      "countries",
      "name",
      "website",
      "address",
      "organizationFields",
    ];
    if (isUsaSelected(state.countries) && state.isNew) keys.push("usState");
    const errors = getErrors(state, keys);

    setValidationError(errors);
  }
  if (type === "business") {
    const keys = [
      "countries",
      "name",
      "website",
      "address",
      "businessFields",
      "category",
    ];
    if (isUsaSelected(state.countries) && state.isNew) keys.push("usState");
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
  if (type === "media" || type === "government") {
    const keys = ["countries", "name", "website", "address"];
    if (isUsaSelected(state.countries) && state.isNew) keys.push("usState");
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
  if (type === "news") {
    const keys = [
      "title",
      "countries",
      "newsFields",
      "body",
      "photo",
      "photoSource",
      "source",
    ];
    const errors = getErrors(state, keys);

    setValidationError(errors);
  }
  if (type === "blog") {
    const keys = [
      "title",
      "countries",
      "newsFields",
      "body",
      "photo",
      "photoSource",
      "blogFields",
    ];
    const errors = getErrors(state, keys);

    setValidationError(errors);
  }
  if (type === "hotSpot") {
    const keys = ["name", "countries", "hotSpotFields", "address"];
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
  if (type === "greenSpot") {
    const keys = ["name", "countries", "greenSpotFields", "address"];
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
};

const cancelValidationError = (
  filterType,
  setValidationError,
  validationError
) => {
  setValidationError(
    validationError.filter((errorItem) => errorItem !== filterType)
  );
};

const getIsRegistered = (spotsByUrl, website) => {
  let isUrlRegistered = false;
  spotsByUrl?.forEach((spot) => {
    if (spot.link === website) {
      isUrlRegistered = true;
    }
  });
  return isUrlRegistered;
};

const checkIsValidFormSteps = ({ state, step }) => {
  const {
    name,
    countries,
    usState,
    address,
    hotSpotFields,
    greenSpotFields,
    type,
    organizationFields,
    website,
    businessFields,
    category,
    spotsByUrl,
  } = state;
  if (type === "NGO") {
    if (step === 1) {
      return !!(
        name &&
        organizationFields.length &&
        website &&
        !getIsRegistered(spotsByUrl, website)
      );
    }
    if (step === 2) {
      return !!(
        countries.length &&
        address &&
        hasRequiredUsState(countries, usState, state.isNew)
      );
    }
    if (step === 3) {
      return true;
    }
  }
  if (type === "business") {
    if (step === 1) {
      return !!(
        name &&
        businessFields.length &&
        category.length &&
        website &&
        !getIsRegistered(spotsByUrl, website)
      );
    }
    if (step === 2) {
      return !!(
        countries.length &&
        address &&
        hasRequiredUsState(countries, usState, state.isNew)
      );
    }
    if (step === 3) {
      return true;
    }
  }
  if (type === "hotSpot") {
    if (step === 1) {
      return !!(countries.length && name && hotSpotFields.length && address);
    }
    if (step === 2) {
      return true;
    }
    if (step === 3) {
      return true;
    }
  }
  if (type === "greenSpot") {
    if (step === 1) {
      return !!(countries.length && name && greenSpotFields.length && address);
    }
    if (step === 2) {
      return true;
    }
    if (step === 3) {
      return true;
    }
  }
  if (type === "government" || type === "media") {
    if (step === 1) {
      return !!(name && website && !getIsRegistered(spotsByUrl, website));
    }
    if (step === 2) {
      return !!(
        countries.length &&
        address &&
        hasRequiredUsState(countries, usState, state.isNew)
      );
    }
    if (step === 3) {
      return true;
    }
  }
};
const updateErrorsFormSteps = ({ state, setValidationError, step }) => {
  const { type } = state;

  if (!type) {
    setValidationError(["type"]);
  }
  if (step === 1) {
    const keys = [];
    if (type === "NGO") keys.push("name", "organizationFields", "website");
    if (type === "hotSpot")
      keys.push("name", "hotSpotFields", "countries", "address");
    if (type === "greenSpot")
      keys.push("name", "greenSpotFields", "countries", "address");
    if (type === "business")
      keys.push("name", "category", "businessFields", "website");
    if (type === "government" || type === "media") keys.push("name", "website");
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
  if (step === 2) {
    const keys = [];
    if (type === "NGO" || type === "business")
      keys.push("countries", "address");
    if (type === "government" || type === "media")
      keys.push("countries", "address");
    if (isUsaSelected(state.countries) && state.isNew) keys.push("usState");
    const errors = getErrors(state, keys);
    setValidationError(errors);
  }
};

export {
  checkIsValidSearch,
  updateErrorsSearch,
  cancelValidationError,
  checkIsValidForm,
  updateErrorsForm,
  checkIsValidFormSteps,
  updateErrorsFormSteps,
  getIsRegistered,
};
